import Cropper from 'cropperjs';
import * as rectLib from '../../../../../../common/maths/geometry/rect';
import { getImageEditorDataObject } from '../../imageModalUtils';
import React from 'react';
import { getIsTransparentImage } from '../../../../../../common/elements/utils/elementPropertyUtils';
import type { ImMNElement } from '../../../../../../common/elements/elementModelTypes';

type RenderableCropper = Cropper & {
    render: () => void;
};

// Cropperjs's typings omit several instance properties the editor relies on.
type CropperInternals = Cropper & {
    element: HTMLImageElement;
    options: Cropper.Options;
    cropped: boolean;
    cropper?: HTMLElement;
};

type CropSelectionData = Pick<Cropper.Data, 'x' | 'y' | 'width' | 'height'>;

/** Rebuilds Cropper geometry after rotate/scale, then restores the intended crop selection. */
export const reflowCropperAfterTransforms = (cropper: Cropper, cropSelection: CropSelectionData): void => {
    (cropper as RenderableCropper).render();
    cropper.crop();
    cropper.setData(cropSelection);
};
export const positionImageInCanvasCenter = (cropper: Cropper) => {
    const { width, height } = cropper.getContainerData();
    const { naturalWidth, naturalHeight } = cropper.getImageData();

    const containerSize = Math.min(width, height);
    const imageSize = Math.min(naturalWidth, naturalHeight);
    const zoomScale = containerSize / imageSize;

    cropper.zoomTo(zoomScale, {
        x: width / 2,
        y: height / 2,
    });
};

type ApplyCropperReadyStateOptions = {
    cropper: Cropper;
    element: ImMNElement;
    applyStoredCropData: boolean;
    pendingCropperDataRef?: React.MutableRefObject<Cropper.Data | null>;
    setEditorIsReady: (ready: boolean) => void;
    dataWasAppliedDuringReplace?: boolean;
    recenterAfterTransforms?: boolean;
};

const applyCropperReadyState = ({
    cropper,
    element,
    applyStoredCropData,
    pendingCropperDataRef,
    setEditorIsReady,
    dataWasAppliedDuringReplace = false,
    recenterAfterTransforms = true,
}: ApplyCropperReadyStateOptions) => {
    const storedEditorData = applyStoredCropData ? getImageEditorDataObject(element) : null;
    const pendingCropperData = pendingCropperDataRef?.current;
    const editorData = pendingCropperData || storedEditorData;

    if (editorData) {
        if (!dataWasAppliedDuringReplace) {
            cropper.clear();
            cropper.setData(editorData);
        }

        // Only restore the crop selection when the stored geometry is complete. Incomplete data (e.g. a
        // legacy element with a partial imageEditorData) would otherwise reflow to a 0×0 box and collapse
        // the selection; skipping the reflow leaves Cropper's default full-image selection intact.
        const hasCompleteCropGeometry =
            Number.isFinite(editorData.x) &&
            Number.isFinite(editorData.y) &&
            Number.isFinite(editorData.width) &&
            Number.isFinite(editorData.height);
        if (hasCompleteCropGeometry) {
            reflowCropperAfterTransforms(cropper, {
                x: editorData.x as number,
                y: editorData.y as number,
                width: editorData.width as number,
                height: editorData.height as number,
            });
        }
        if (recenterAfterTransforms) positionImageInCanvasCenter(cropper);
    }

    if (pendingCropperDataRef) pendingCropperDataRef.current = null;
    setEditorIsReady(true);
};

const getImageModalEditorCropperOptions = ({
    element,
    canUseLoadedImage,
    isDirtyRef,
    onCropChange,
    onReady,
}: {
    element: ImMNElement;
    canUseLoadedImage: () => boolean;
    isDirtyRef: React.MutableRefObject<boolean>;
    onCropChange?: () => void;
    onReady: () => void;
}): Cropper.Options => ({
    dragMode: 'none',
    viewMode: 2,
    background: getIsTransparentImage(element),
    center: false,
    movable: false,
    zoomOnTouch: false,
    zoomOnWheel: false,
    toggleDragModeOnDblclick: false,
    autoCrop: true,
    autoCropArea: 1,
    checkOrientation: false,
    ready: () => {
        if (canUseLoadedImage()) onReady();
    },
    cropmove: () => {
        isDirtyRef.current = true;
        onCropChange?.();
    },
});

type LoadCanvasIntoCropperOptions = {
    imageRef: React.RefObject<HTMLImageElement | null>;
    cropperRef: React.MutableRefObject<Cropper | null>;
    element: ImMNElement;
    setEditorIsReady: (ready: boolean) => void;
    isDirtyRef: React.MutableRefObject<boolean>;
    canUseLoadedImage?: () => boolean;
    applyStoredCropData?: boolean;
    // When a background-removal toggle swaps the underlying source variant, the user's in-progress
    // rotate/flip/crop must survive the reload. The caller snapshots the live `cropper.getData()` here so
    // it can be re-applied to the swapped-in image instead of falling back to the stored `imageEditorData`.
    pendingCropperDataRef?: React.MutableRefObject<Cropper.Data | null>;
    onCropChange?: () => void;
    recenterAfterTransforms?: boolean;
};

/**
 * Initialises Cropper for the loaded editor image.
 *
 * `canUseLoadedImage` protects editor readiness from stale Cropper instances. Cropper's `ready`
 * callback can fire after React has already started loading a newer image.
 */
export const loadCanvasIntoCropper = ({
    imageRef,
    cropperRef,
    element,
    setEditorIsReady,
    isDirtyRef,
    canUseLoadedImage = () => true,
    applyStoredCropData = true,
    pendingCropperDataRef,
    onCropChange,
    recenterAfterTransforms = true,
}: LoadCanvasIntoCropperOptions) => {
    if (!imageRef.current) return;

    const onReady = () => {
        if (!cropperRef.current) return;
        applyCropperReadyState({
            cropper: cropperRef.current,
            element,
            applyStoredCropData,
            pendingCropperDataRef,
            setEditorIsReady,
            recenterAfterTransforms,
        });
    };

    cropperRef.current = new Cropper(
        imageRef.current,
        getImageModalEditorCropperOptions({ element, canUseLoadedImage, isDirtyRef, onCropChange, onReady }),
    );
};

type ReplaceCropperImageOptions = {
    cropperRef: React.MutableRefObject<Cropper | null>;
    imageUrl: string;
    element: ImMNElement;
    setEditorIsReady: (ready: boolean) => void;
    isDirtyRef: React.MutableRefObject<boolean>;
    canUseLoadedImage: () => boolean;
    applyStoredCropData: boolean;
    pendingCropperDataRef?: React.MutableRefObject<Cropper.Data | null>;
    onCropChange?: () => void;
    recenterAfterTransforms?: boolean;
};

/** Replaces a decoded source on the existing Cropper instance while preserving the editor surface. */
export const replaceCropperImage = ({
    cropperRef,
    imageUrl,
    element,
    setEditorIsReady,
    isDirtyRef,
    canUseLoadedImage,
    applyStoredCropData,
    pendingCropperDataRef,
    onCropChange,
    recenterAfterTransforms = true,
}: ReplaceCropperImageOptions) => {
    const cropper = cropperRef.current;
    if (!cropper) return;

    const cropperInternals = cropper as CropperInternals;
    const storedEditorData = applyStoredCropData ? getImageEditorDataObject(element) : null;
    const replacementData = pendingCropperDataRef?.current || storedEditorData;
    const sourceImage = cropperInternals.element;
    const editor = sourceImage.closest('.editor');
    const cover = editor instanceof HTMLElement ? document.createElement('div') : null;
    if (cover && editor) {
        cover.className = 'cropper-source-replace-cover';
        cover.setAttribute('aria-hidden', 'true');

        const outgoingCropperDom = applyStoredCropData ? cropperInternals.cropper : null;
        if (outgoingCropperDom) {
            // Freeze the outgoing frame verbatim: a static clone of the live cropper DOM carries the exact
            // canvas geometry and rotate/flip transform on screen, so the cover is indistinguishable from
            // the frame it is masking.
            cover.appendChild(outgoingCropperDom.cloneNode(true));
        } else {
            // No rendered outgoing frame to freeze — contain-fit the incoming source to the (pinned) box.
            const coverImage = document.createElement('img');
            coverImage.className = 'cropper-source-replace-cover-image';
            coverImage.src = imageUrl;
            coverImage.alt = '';
            cover.appendChild(coverImage);
        }
        editor.appendChild(cover);
    }

    const onReady = () => {
        if (cropperRef.current && canUseLoadedImage()) {
            applyCropperReadyState({
                cropper: cropperRef.current,
                element,
                applyStoredCropData,
                pendingCropperDataRef,
                setEditorIsReady,
                dataWasAppliedDuringReplace: !!replacementData,
                recenterAfterTransforms,
            });
        }
        cover?.remove();
    };

    Object.assign(
        cropperInternals.options,
        getImageModalEditorCropperOptions({ element, canUseLoadedImage, isDirtyRef, onCropChange, onReady }),
    );
    cropper.replace(imageUrl, false);
    if (replacementData) cropperInternals.options.data = replacementData;
};

export const adjustCropperForRotation = (
    cropper: Cropper,
    modalWidth: number,
    initialCropData: Cropper.CropBoxData,
) => {
    const initialContainerData = cropper.getContainerData();

    // The modal might change scale when it gets rotated due to the a wide or tall aspect ratio.
    // We need to find out the scale change so that we can convert the initial crop rectangle into
    // the final crop rectangle
    const scalingFactor = modalWidth / rectLib.getHeight(initialContainerData);
    // Force Cropper to render with the new rotation before updating the crop box.
    // `render()` exists at runtime even though it is not exposed on the public type.
    (cropper as RenderableCropper).render();

    let newCropRect = {
        top: initialContainerData.width - initialCropData.left - initialCropData.width,
        left: initialCropData.top,
        width: initialCropData.height,
        height: initialCropData.width,
    };
    newCropRect = rectLib.asRect(newCropRect);
    // @ts-ignore - rectlib.scale uses lodash curry, which breaks the type inference
    newCropRect = rectLib.scale(scalingFactor, newCropRect);

    cropper.setCropBoxData({
        top: rectLib.getTop(newCropRect),
        left: rectLib.getLeft(newCropRect),
        width: rectLib.getWidth(newCropRect),
        height: rectLib.getHeight(newCropRect),
    });
};

/** Mirrors the crop box horizontally so a V2 screen-space flip keeps selecting the same content. */
export const adjustCropperForHorizontalFlip = (cropper: Cropper): void => {
    if (!(cropper as CropperInternals).cropped) return;

    const { width: containerWidth } = cropper.getContainerData();
    const cropBox = cropper.getCropBoxData();
    cropper.setCropBoxData({
        left: containerWidth - cropBox.left - cropBox.width,
        top: cropBox.top,
        width: cropBox.width,
        height: cropBox.height,
    });
};

/** Mirrors the crop box vertically so a V2 screen-space flip keeps selecting the same content. */
export const adjustCropperForVerticalFlip = (cropper: Cropper): void => {
    if (!(cropper as CropperInternals).cropped) return;

    const { height: containerHeight } = cropper.getContainerData();
    const cropBox = cropper.getCropBoxData();
    cropper.setCropBoxData({
        left: cropBox.left,
        top: containerHeight - cropBox.top - cropBox.height,
        width: cropBox.width,
        height: cropBox.height,
    });
};
